// **Welcome!**
//This file is your location to update brand specific data used throughout the application. Please also see the theme file for colors, typography, and additional MUI component customization.

//For wallet page coins displayed, scroll down to pages/dashboard and updated the walletCoins array. Set useBrandCoin to false if you do not yet have your private blockchain coin set up. This will remove it from the wallet dashboard, as well as remove user references to bridging for that coin (page and nav bars)

//logos
import brandIcon from "../assets/icons/logos/adventurewurld.svg";
import brandLogoWithText from "../assets/icons/logos/adventurewurld_full_logo.png";
import brandLogo from "../assets/icons/logos/adventurewurld.svg";
import brandTokenIcon from "../assets/icons/logos/adventurewurld-token.svg";
import brandPrivateTokenIcon from "../assets/icons/logos/adventurewurld-native.svg";
import awBanner from "../assets/icons/logos/aw_banner.png";
import awLogo from "../assets/icons/logos/aw_logo.png";
import awNode from "../assets/icons/logos/aw_node.png";

//images
import pageNotFoundImage from "@/images/page-not-found.png";
import serverErrorImage from "@/images/server-error.png";
import twitterLogo from "@/icons/logos/twitter.svg";
import facebookLogo from "@/icons/logos/facebook.svg";
import linkedInLogo from "@/icons/logos/linkedin.svg";
import discordLogo from "@/icons/logos/discord.svg";
import youtubeLogo from "@/icons/logos/youtube.svg";
import instagramLogo from "@/icons/logos/instagram.svg";

// Fallback Images (shared)
import noReferrals from "@/images/refer-a-friend_no-referrals.svg";
import blockFabric from "@/icons/logos/codex.svg";
import blockFabricHash from "@/icons/logos/codex-icon.svg";
import votingBanner from "@/images/voting.svg";

const brandName = "AdventureWurld";
const brandChainName = "AdventureWurld";
const brandCoin = "GULD";
const staticSite = "https://adventurewurld.com";
const rpcUrl = "https://rpc.adventurewurld.blockfabric.net";
const domain = "adventurewurld.com";
const version = "v2.6.3";
const binaryBrand = "adventurewurld";
const contractAddress = "";
const chainID = 0;
const appUrl = "https://core-app.adventurewurld.com";
const bridgeFee = 1000;
const useV2Endpoints = true;

// Node Governance URL
const nodeGovUrl = `https://adventurewurld.node-governance.io`;

export const brand = {
  // Brand Info
  brandName,
  brandChainName,
  staticSite,
  appUrl,
  /** Open Graph / link preview (absolute URL) */
  ogImageUrl: new URL(awLogo.src, appUrl).href,
  /** Full-width logo on sign-up (replaces compact authenticationLogo for this brand) */
  signupFormLogo: brandLogoWithText.src,
  /** Referral / Smart Node copy: minimum purchase line in home + share modal */
  referralMinimumSmartNodePurchase: "$3,000",
  referralShareOfferThirdBullet:
    "For the first three referrals that sign up using your unique, shareable offer link, you will receive a credit of $500 towards the purchase of a Smart Node (3 free credits max per account, minimum $3,000 purchase required).",
  nodeGovUrl,
  rpcUrl,
  contractAddress,
  chainID,
  bridgeFee,
  walletMigrationDate: "Dec 25, 2025",
  coin: brandCoin,
  useV2Endpoints,
  // Logos
  brandLogoHorizontalLight: brandLogoWithText.src,
  brandLogoCompactLight: brandLogo.src,
  brandLogoSymbolLight: brandLogo.src,
  brandLogoHorizontalDark: brandLogoWithText.src,
  brandLogoCompactDark: brandLogo.src,
  brandLogoSymbolDark: brandLogo.src,
  authenticationLogo: brandLogo.src,
  logoWithText: brandLogoWithText.src,
  logo: brandLogo.src,
  icon: brandIcon.src,
  tokenIconL2: brandPrivateTokenIcon.src,
  tokenIcon: brandTokenIcon.src,
  // Fallback Images (PNG in assets/icons/logos)
  fallbackImages: {
    shareHero: awLogo.src,
    shareSignup: awLogo.src,
    setupWallet: brandIcon.src,
    noReferrals: noReferrals.src,
    blockFabric: blockFabric.src,
    blockFabricHash: blockFabricHash.src,
    nodeBanner: awNode.src,
    votingBanner: votingBanner.src,
  },
  loggedOutHomeRoute: "/",
  loggedInHomeRoute: "/home",
  previousSiteURL: "",
  blockFabricUrl: "https://blockfabric.host/?referralCode=AdventureWurld-NE",
  blockFabricTxnUrl:
    "https://explore.mainnet.adventurewurld.blockfabric.net/transactions/",
  externalWalletEthAddress: "https://etherscan.io/address/",
  support: {
    url: "https://help.adventurewurld.com/",
    target: "_blank",
  },
  nftSupportLinks: {
    boost: '',
    activatable: '',
    carbonCredit: '',
  },
  storeFilterCategories: {
    nodes: "category-2",
    other: "category-1",
  },
  cookiesPreferencesKey: "NerdCookiesPreferences",
  cookiesDomain:
    process.env.NEXT_PUBLIC_ENVIRONMENT === "local"
      ? "localhost"
      : ".adventurewurldunited.com",
  cookiesExpiration: new Date(Date.now() + 300000), // 5 minutes
  nodeSetupBashCommands: {
    vps: `bash <(curl -s https://core-download.${domain}/latest/linux.sh)`,
    mac: `bash <(curl -s https://core-download.${domain}/latest/mac.sh)`,
    linux: {
      sudo1: 'sudo chown -R "{your_username}:" /usr/local/bin',
      sudo2: 'sudo chown -R "{your_username}:" /etc/systemd/system',
      bash: `bash <(curl -s https://core-download.${domain}/latest/linux.sh)`,
      sudo3: `sudo systemctl start ${binaryBrand}`,
      sudo4: `systemctl status ${binaryBrand}`,
    },
  },
  helpDoc:
    "https://core-sdk-dev-public-temp.s3.us-west-2.amazonaws.com/docs/BrandNodeHelpDocument.pdf",

  ssocialMediaSites: {
    twitter: {
      url: "https://x.com/AdventureWurld",
      icon: twitterLogo.src,
      isActive: true,
      buttonBackgroundColor: "#00acee",
    },
    discord: {
      url: "http://discord.gg/adventurewurld",
      icon: discordLogo.src,
      isActive: true,
      buttonBackgroundColor: "#7289D9",
    },
    linkedIn: {
      url: "https://linkedin.com",
      icon: linkedInLogo.src,
      isActive: true,
      buttonBackgroundColor: "#0a66c2",
    },
    facebook: {
      url: "https://facebook.com",
      icon: facebookLogo.src,
      isActive: true,
      buttonBackgroundColor: "#1877f2",
    },
    instagram: {
      url: "https://instagram.com",
      icon: instagramLogo.src,
      isActive: true,
      buttonBackgroundColor: "transparent",
    },
    youtube: {
      url: "https://youtube.com",
      icon: youtubeLogo.src,
      isActive: true,
      buttonBackgroundColor: "#C4302B",
    },
  },
  errorMessages: {
    genericError: "Something has gone wrong, please try again later.",
    inputValidationFields: {
      name: "Improperly formatted name",
      email: "Enter a valid email (john@example.com)",
      password: "Password does not meet the character requirement",
      confirmPassword: "Passwords do not match",
      checkbox: "You must be over 18 to sign up.",
      phone: "Improperly formatted phone number",
    },
    birthDateError: "Invalid Date Format, please use MM/DD/YYYY",
    birthDateAge: "Please check the year",
  },
  pages: {
    login: {
      title: "Login to Your AdventureWurld Account",
    },
    dashboard: {
      walletCoins: ["ETH", "BTC"],
      useBrandCoin: true, // set to true when private blockchain coin is set up
    },
    referFriend: {
      howPointsAreAwarded: `Explanation about how many points are awarded by each node and signup (decided by ${brandName}).`,
    },
    home: {
      hideMoreOffersComingSoonCard: true,
      hideNoReferralsIllustration: true,
      defaultShareOfferImage: awLogo.src,
    },
    voting: {
      charterKey: "",
      charterFile: "",
      charterPageQuantity: 0,
      /** Hide hero image on /voting (Core-App doc) */
      hideVotingBanner: true,
    },
    errorPages: {
      serverFailed: {
        title: "500 Internal server error.",
        subText:
          "We are unable to complete this request. Please try again later.",
        notRecordedImage: serverErrorImage.src,
      },
      infoPageOffline: {
        title: "You are currently offline.",
        subText: "Please check your internet connection and try again.",
        image: pageNotFoundImage.src,
      },
    },
  },
};
